/**
 * Sector Operations — Clinics Legal Roles on Rosters.
 * R39 M3.a (plan docs/plans/sector-operations.md §2 SO-P261, SO-D234).
 */

import type { OpsComplianceCategory } from './compliance.js'
import type { ClinicsPreset } from './sectors.js'

export const CLI_LEGAL_ROLE_KEYS = [
  'REGISTERED_PHARMACIST',
  'PCPNDT_DOCTOR',
  'RADIATION_SAFETY_OFFICER',
  'EMBRYOLOGIST',
  'DIALYSIS_TECHNICIAN',
  'REGISTERED_MEDICAL_PRACTITIONER',
] as const

export type CliLegalRoleKey = (typeof CLI_LEGAL_ROLE_KEYS)[number]

export interface CliLegalRoleDef {
  readonly key: CliLegalRoleKey
  readonly labelKey: string
  readonly category: OpsComplianceCategory
}

export const CLI_LEGAL_ROLES: Record<CliLegalRoleKey, CliLegalRoleDef> = {
  REGISTERED_PHARMACIST: {
    key: 'REGISTERED_PHARMACIST',
    labelKey: 'NLSOpsCliLegalRoleRegisteredPharmacist',
    category: 'REGISTRATION',
  },
  PCPNDT_DOCTOR: {
    key: 'PCPNDT_DOCTOR',
    labelKey: 'NLSOpsCliLegalRolePcpndtDoctor',
    category: 'REGISTRATION',
  },
  RADIATION_SAFETY_OFFICER: {
    key: 'RADIATION_SAFETY_OFFICER',
    labelKey: 'NLSOpsCliLegalRoleRadiationSafetyOfficer',
    category: 'REGISTRATION',
  },
  EMBRYOLOGIST: {
    key: 'EMBRYOLOGIST',
    labelKey: 'NLSOpsCliLegalRoleEmbryologist',
    category: 'REGISTRATION',
  },
  DIALYSIS_TECHNICIAN: {
    key: 'DIALYSIS_TECHNICIAN',
    labelKey: 'NLSOpsCliLegalRoleDialysisTechnician',
    category: 'REGISTRATION',
  },
  REGISTERED_MEDICAL_PRACTITIONER: {
    key: 'REGISTERED_MEDICAL_PRACTITIONER',
    labelKey: 'NLSOpsCliLegalRoleRegisteredMedicalPractitioner',
    category: 'REGISTRATION',
  },
}

export function isLegalRoleKey(val: unknown): val is CliLegalRoleKey {
  return typeof val === 'string' && CLI_LEGAL_ROLE_KEYS.includes(val as CliLegalRoleKey)
}

export const CLI_PRESET_LEGAL_ROLES: Record<ClinicsPreset, readonly CliLegalRoleKey[]> = {
  SINGLE_DOCTOR_FAMILY: ['REGISTERED_MEDICAL_PRACTITIONER'],
  POLYCLINIC: ['REGISTERED_MEDICAL_PRACTITIONER'],
  MATERNITY_CHILD: ['REGISTERED_MEDICAL_PRACTITIONER'],
  MENTAL_HEALTH: ['REGISTERED_MEDICAL_PRACTITIONER'],
  AYUSH_WELLNESS: ['REGISTERED_MEDICAL_PRACTITIONER'],
  VACCINATION: ['REGISTERED_MEDICAL_PRACTITIONER'],
  DENTAL: [],
  EYE_OPTICAL: [],
  SKIN_COSMETIC_HAIR: [],
  IVF: ['EMBRYOLOGIST'],
  PHYSIO_REHAB: [],
  DIALYSIS: ['DIALYSIS_TECHNICIAN'],
  DAY_CARE_NURSING_HOME: [],
  PATHOLOGY_LAB: [],
  COLLECTION_CENTRE: [],
  HOME_COLLECTION: [],
  IMAGING_CENTRE: ['PCPNDT_DOCTOR', 'RADIATION_SAFETY_OFFICER'],
  BLOOD_BANK: [],
  PHARMACY_CHAIN: ['REGISTERED_PHARMACIST'],
  PHARMA_DEVICE_DISTRIBUTOR: ['REGISTERED_PHARMACIST'],
  VET_CLINIC: [],
}
